import { API_BASE_URL } from '../../services/config.js';
import { storageService } from '../../domain/services/StorageService.js';

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';
  }
}

export const fetchClient = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const token = storageService.getAuthToken();
  if (token && !options.skipAuth) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { message: response.statusText };
      }
      throw new ApiError(errorData.message || 'API Error', response.status, errorData);
    }

    if (response.status === 204) {
      return null;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return await response.text();
  } catch (error) {
    throw error;
  }
};

export default {
  get: (endpoint, options) => fetchClient(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, body, options) => fetchClient(endpoint, { ...options, method: 'POST', body: JSON.stringify(body) }),
  put: (endpoint, body, options) => fetchClient(endpoint, { ...options, method: 'PUT', body: JSON.stringify(body) }),
  patch: (endpoint, body, options) => fetchClient(endpoint, { ...options, method: 'PATCH', body: JSON.stringify(body) }),
  delete: (endpoint, options) => fetchClient(endpoint, { ...options, method: 'DELETE' }),
  /** Upload FormData (multipart) — skips JSON Content-Type */
  upload: (endpoint, formData, options) => {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const token = storageService.getAuthToken();
    return fetch(url, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options?.headers,
      },
      body: formData,
    }).then(async (response) => {
      if (!response.ok) {
        let errorData;
        try { errorData = await response.json(); } catch { errorData = { message: response.statusText }; }
        throw new ApiError(errorData.message || 'Upload Error', response.status, errorData);
      }
      return response.json();
    });
  },
};
